import { useNavigate } from "react-router-dom"
import { ILinkedInUserData, IUser } from "../../../interfaces/models/user"
import { IApiKeys, IMemberApiKey } from "../../../interfaces/models/workspace"
import { isOnboardingSurveyCompleted } from "../../../pages/onboarding/onboardingSurveyUtils"
import { clearSkippedToAllSet } from "../../../pages/onboarding/onboardingExtensionSkipUtils"
import { hasFinishedMobileOnboarding } from "../../../pages/onboarding/onboardingMobileUtils"
import { saveLastLoginUser } from "../../../pages/authentication/lastLoginUserStorage"
import { IAuthData } from "../../../interfaces/store/authStore"
import { useAppActions } from "../../../stores/application/useAppActions"
import { useAuthActions } from "../../../stores/auth/useAuthActions"
import { useAuthRequest } from "../../apis/auth/useAuthRequest"
import { useConversationsRequests } from "../../apis/conversations/useConversationsRequests"
import { useExtensionRequest } from "../../apis/extension/useExtensionRequest"
import { IOnboardingData, useUserRequest } from "../../apis/user/useUserRequest"
import { IWorkspaceCreateParams, useWorkspaceRequest } from "../../apis/workspace/useWorkspaceRequest"
import { AppLiterals, headerRoutes } from "../../../interfaces/store/appStore"
import { useAppStore } from "../../../stores/application/useAppStore"
import { IUserWorkspace } from "../../../interfaces/response/workspace"
import { useAuthStore } from "../../../stores/auth/useAuthStore"
import { useAppHandlers } from "../app/useAppHandlers"
import moment from "moment"
import { useTagsHandlers } from "../tags/useTagsHandlers"
import { useNotesHandlers } from "../notes/useNotesHandlers"
import { useTasksHandlers } from "../tasks/useTasksHandlers"
import { useTemplateHandlers } from "../template/useTemplateHandlers"
import { useBillingHandlers } from "../billing/useBillingHandlers"
import { useWorkspaceHandlers } from "../workspace/useWorkspaceHandlers"
import { useBillingActions } from "../../../stores/billing/useBillingActions"
import { useConnectionsHandlers } from "../connections/useConnectionsHandlers"
import { extractJobTriples, parsePeopleFromSdui, getPaginationData, parseMutualConnectionsSearchResponse, isMobileDevice } from "../../../utils"
import { useJobChangesHandlers } from "../jobChanges/useJobChangesHandlers"

export const useAuthHandlers = () => {

    const { logout,
        setAuthData,
        setUser,
        setContext,
        setLifetime,
        setTrial,
        setValidSubscription,
        setWorkspace,
        setWorkspaceOwner,
        setWorkspaceAdmin,
        setSubscription,
        syncAccesToken,
        syncRefreshToken,
        setUserWorkspaces,
        setDisablePremiumFeatures,
        setDisconnectedConnectionsCount,
        setIsLoggedToLinkedin,
        setScheduledRemoval,
        setVerifyEmailRedirected,
        updateUserLastFetchPostsDate
    } = useAuthActions()
    const { getUser, switchUserWorkspace, generateAPIKey, revokeAPIKey, getAPIKeys, generateMemberAPIKey, revokeMemberAPIKey, deleteMemberAPIKey, logout: logoutApi, workOSLogoutUrl, sendDesktopLink } = useAuthRequest()
    const { getLoggedInLIUser, putSessionTokens, setUserLoggedAuth, syncConnections, syncConversations, getLDAccessToken, getExtensionStatus, getJobChanges, searchPeopleSdui, getProfileByIdentityDash, mutualConnectionsFromLinkedinTab } = useExtensionRequest()
    const { getInboxQueryParams } = useConversationsRequests()
    const { setOnboardingStep, setHeaderState, setSyncCompletionPercentage, setExtensionInstalled, setExtensionVersion } = useAppActions()
    const { createWorkspace, getInitialWorkspaceSync } = useWorkspaceRequest()
    const { postOnboardingData, getWorkspaces, saveLinkedInData, saveUserPreferences, saveTagsAndNotesSettings, canDeleteUserAccount, deleteUserAccount, updateLastFetchPostsDate } = useUserRequest()
    const { store: { extensionInstalled, onboardingStep: currentOnboardingStep } } = useAppStore()
    const { store: { workspace, user, userWorkspaces, authData, syncLinkedInContactsTaskId } } = useAuthStore()
    const navigate = useNavigate()
    const { showInfoToast, showErrorToast, showSuccessToast, showSpinner, hideSpinner } = useAppHandlers()
    const {resetTagsStoreHandler} = useTagsHandlers();
    const {resetNotesStoreHandler} = useNotesHandlers();
    const { resetTasksStoreHandler, completeSingleTask } = useTasksHandlers();
    const {resetTemplatesStoreHandler} = useTemplateHandlers();
    const {resetBillingStore} = useBillingActions();
    const {resetWorkspaceUsersStoreHandler} = useWorkspaceHandlers();
    const {resetConnectionsStoreHandler, setScheduledRemovalParametersHandler, getConnectionsHandler, getConnectionsByPublicIdentifiersHandler, updateLinkedinMutualProfilesHandler} = useConnectionsHandlers();
    const {setJobChangesHandler} = useJobChangesHandlers();

    const getMeHandler = async () => {
        const lastSyncAt = localStorage.getItem('lastSyncAt')

        while (workspace && !workspace?.initialSync && lastSyncAt) {
            if (!localStorage.getItem('lastSyncAt')) break
    
            const result = await getInitialWorkspaceSync()
            const { workspaceInitialSync, userDetails, syncInProgress, syncCompletionPercentage } = result

            setSyncCompletionPercentage(syncCompletionPercentage)

            if (workspaceInitialSync) {
                setUser(userDetails?.user)
                setWorkspace({...workspace, initialSync: workspaceInitialSync})
                getConnectionsHandler()
                setSyncCompletionPercentage(0)
                await getJobChangesHandler()
                break
            }

            await new Promise(resolve => setTimeout(resolve, 2000))
        }
    }
    
    const setAuthDataHandler = async (payload: IAuthData, extensionData?: boolean) => {
        let accessToken = payload?.accessToken ?? localStorage.getItem('at') as any //check this
        syncAccesToken(accessToken)
        let refreshToken = payload?.refreshToken ?? localStorage.getItem('rt') as any 
        syncRefreshToken(refreshToken)

        if (accessToken) {
            setAuthData({ accessToken, refreshToken })
        }

        await getUserHandler({ accessToken, refreshToken }, extensionData)

        // add to the extension
        await putSessionTokens({accessToken, refreshToken})
    }

    const isExtensionInstalledResponse = (value: unknown): value is { status: string; version?: string } =>
        Boolean(value && typeof value === 'object' && (value as { status?: string }).status === 'installed')

    const getUserHandler = async (payload?: IAuthData, extensionData?: boolean) => {
        let userLI: ILinkedInUserData | undefined
        let hasExtension = Boolean(extensionData || extensionInstalled)
        let extStatus: unknown = null

        if (!hasExtension) {
            try {
                extStatus = await getExtensionStatus()
                if (isExtensionInstalledResponse(extStatus)) {
                    hasExtension = true
                    setExtensionInstalled(true)
                    if (extStatus.version) setExtensionVersion(extStatus.version)
                }
            } catch {
                /* extension not available */
            }
        }

        if (hasExtension) {
            userLI = await getLoggedInLIUserHandler()
            if (!isExtensionInstalledResponse(extStatus)) {
                try {
                    extStatus = await getExtensionStatus()
                    if (isExtensionInstalledResponse(extStatus) && extStatus.version) {
                        setExtensionVersion(extStatus.version)
                    }
                } catch {
                    /* ignore */
                }
            }
        }

        const result = await getUser(payload)
        const extensionFlag = hasExtension ? true : extensionData
        setUserHandler(result, extensionFlag, { authData: payload }, userLI)
    }

    const setUserHandler = async (result: any, extensionData?: boolean, authData?: any, userLI?: ILinkedInUserData) => {
        extensionData = extensionData ? extensionData : extensionInstalled
        setUser(
            result?.user
                ? {
                      ...result.user,
                      preferences: {
                          ...result.user.preferences,
                          ...(user?.preferences?.mobileOnboardingFinished
                              ? { mobileOnboardingFinished: true }
                              : {}),
                      },
                  }
                : result?.user,
        )
        setContext(result?.context === "personal" || !result?.context ? "individual" : result?.context) //todo: remove personal
        const isLifetime = (result?.hasCouponApplied || result?.user?.plan === "lifetime") && result?.workspace?.isPersonal;
        setLifetime(isLifetime)
        setTrial(result?.isOnTrialPeriod > 0)
        setValidSubscription(result?.isValidSubscription)
        setWorkspace(result?.workspace)
        setDisablePremiumFeatures((!(result?.isOnTrialPeriod > 0) && !result?.isValidSubscription && !isLifetime) || result?.subscription?.pausedByAdmin)
        setWorkspaceOwner(result?.isWorkspaceOwner ?? false)
        setWorkspaceAdmin(result?.isWorkspaceAdmin ?? false)
        const surveyCompleted = isOnboardingSurveyCompleted(result?.user)
        const hasLinkedInProfile = Boolean(result?.user?.linkedinData?.publicIdentifier)
        const linkedInMismatch =
            Boolean(userLI?.publicIdentifier) &&
            Boolean(result?.user?.linkedinData?.publicIdentifier) &&
            userLI!.publicIdentifier !== result.user.linkedinData.publicIdentifier

        const userId = result?.user?._id
        const isMobile = isMobileDevice()
        const mobileOnboardingFinished = hasFinishedMobileOnboarding(result?.user, userId) || hasFinishedMobileOnboarding(user, userId)

        let onboardingStep: number
        if (isMobile && !extensionData) {
            if (mobileOnboardingFinished) {
                onboardingStep = -2
            } else if (currentOnboardingStep < 0 && surveyCompleted) {
                onboardingStep = -2
            } else if (surveyCompleted) {
                onboardingStep = 5
            } else {
                onboardingStep = 4
            }
        } else if (currentOnboardingStep === 5) {
            onboardingStep = 5
        } else if (result?.newUser && !extensionData) {
            onboardingStep = 1
        } else if (!extensionData) {
            onboardingStep = -2
        } else if (!hasLinkedInProfile || linkedInMismatch) {
            onboardingStep = surveyCompleted ? -2 : 2
        } else if (!surveyCompleted) {
            onboardingStep = 3
        } else {
            onboardingStep = -2
            clearSkippedToAllSet()
        }

        if (
            !isMobile &&
            !extensionData &&
            currentOnboardingStep >= 2 &&
            currentOnboardingStep <= 3
        ) {
            onboardingStep = 1
        } else if (
            !isMobile &&
            currentOnboardingStep >= 1 &&
            onboardingStep >= 1 &&
            onboardingStep < currentOnboardingStep
        ) {
            onboardingStep = currentOnboardingStep
        } else if (
            !isMobile &&
            currentOnboardingStep >= 1 &&
            currentOnboardingStep < 5 &&
            onboardingStep === -2 &&
            !extensionData
        ) {
            onboardingStep = currentOnboardingStep
        }

        setOnboardingStep(onboardingStep)
        setHeaderState(!result || onboardingStep >= 0 ? {} : (
            {
                ...headerRoutes, 
                inbox: {
                    ...headerRoutes['inbox'], 
                    visible: result
                },
                integration: {
                    ...headerRoutes['integrations'],
                    visible: result?.isWorkspaceOwner || result?.isWorkspaceAdmin
                }}
        ))
        if(result) setSubscription(result?.subscription)
        if(result?.user){
            setLastSyncAt(result.user.lastSyncAt ?? 0)
            if(result?.user?.isSynced && extensionData) doAutomaticSync(result?.user)
        }

        if(result?.scheduledRemoval){
        //    if(result?.scheduledRemoval?.inProgress
        //        && result?.scheduledRemoval?.total > result?.scheduledRemoval?.count){
                setScheduledRemoval(result?.scheduledRemoval);
                setScheduledRemovalParametersHandler(result?.scheduledRemoval);
        //    }
        }

        //Set user in extension chrome storage
        await setUserLoggedAuth({
            user: {
                context: 'workspace',
                csrfToken: authData?.accessToken,
                refreshToken: authData?.refreshToken,
                linkedInUserData: {...result?.user?.linkedinData},
                disablePremiumFeatures: (!(result?.isOnTrialPeriod > 0) && !result?.isValidSubscription && !isLifetime) || result?.subscription?.pausedByAdmin,
                ...result
            }
        })
    }

    //todo: not to be confused with the regular create that should not set workspace as the currently logged in, the other one needs to go to worspace handlers
    const createAndLoginToWorkspaceHandler = async (payload: IWorkspaceCreateParams, additionalHeaders?: {[key: string]: string; } | undefined) => {
        const result = await createWorkspace(payload, false, additionalHeaders);
        if(result) {
            const token = (await switchUserWorkspace({
                context: AppLiterals.WORKSPACE,
                workspaceId: result._id
            }, additionalHeaders))?.token;
            if (!token) return;
            await setAuthDataHandler({
                accessToken: `Bearer ${token}`,
                refreshToken: additionalHeaders?.['refresh-token'],
            });
            return true
        } else {
            return false
        }
    }

    const changeUserWorkspace = async (newWorkspace: IUserWorkspace) => {
        if (!newWorkspace || newWorkspace.workspaceId === workspace?._id) return; 

        // reset stores
        resetAllStores();
        
        // Get context access token 
        const token = (await switchUserWorkspace({
                context: newWorkspace.workspaceId ? AppLiterals.WORKSPACE : AppLiterals.INDIVIDUAL,
                ...(newWorkspace.workspaceId ? { workspaceId: newWorkspace.workspaceId } : {}),
            }))?.token;
        if (!token) return;

        await setAuthDataHandler({ accessToken: `Bearer ${token}`});
        
        navigate(0);
    }

    const createWorkspaceHandler = async (payload: IWorkspaceCreateParams) => {
        const result = await createWorkspace(payload);
        if(result) {
            await getUserWorkspacesHandler();
            return true
        } else {
            return false
        }
        
    }

    const saveOnboardingData = async (payload: IOnboardingData) => {
        const result = await postOnboardingData(payload);
        if(result) {
            let userLI
            if(extensionInstalled) {
                userLI = await getLoggedInLIUserHandler()
            } else if (payload.startedExtensionInstallation !== false) {
                getUserHandler();
            }
            setUserHandler(result, undefined, undefined, userLI)
        }
    }

    // onError gets the server's message, e.g. that this LinkedIn is already connected to too many accounts.
    const saveLinkedInDataToUser = async (payload: ILinkedInUserData, onError?: (message?: string) => void) => {
        const result = await saveLinkedInData({ userLinkedInInfo: payload }, onError)
        if(result) {
            setUserHandler(result, undefined, undefined, payload)
            return result
        }
        return undefined
    }

    const getLoggedInLIUserHandler = async (): Promise<ILinkedInUserData | undefined> => {
        const result = await getLoggedInLIUser()
        setIsLoggedToLinkedin(result ? true : false)
        return result ?? undefined
    }

    const getUserWorkspacesHandler = async () => {
        const result = await getWorkspaces()
        if(result) {
            setUserWorkspaces([...(result?.workspaces || [])])
            const selectedWorkspace = result?.workspaces.find((userWorkspace: any) => workspace._id === userWorkspace.workspaceId)
            selectedWorkspace && setWorkspace({...workspace, showAutoTags: selectedWorkspace.showAutoTags })
        } 
        
    }

    // --- Sync limits helpers ---
    const getSyncHistoryKey = (): string => {
        const workspaceId = (workspace as any)?._id || 'no-workspace';
        return `syncEvents:${workspaceId}`;
    };

    const getPlanDailyLimit = (): number | null => {
        // Starter: 3/day; Pro: 6/day; Business: no cap (null)
        if ((workspace as any)?.isStarter) return 3;
        if ((workspace as any)?.isBusiness) return null;
        return 6; // Pro and others default to 6/day
    };

    const getSyncLimitInfo = () => {
        const limit = getPlanDailyLimit();
        const key = getSyncHistoryKey();
        let events: { t: string; type?: 'auto'|'manual' }[] = [];
        try {
            const raw = window.localStorage.getItem(key);
            events = raw ? JSON.parse(raw) : [];
        } catch {}
        const now = moment();
        // keep only last 24h
        const recent = events.filter(e => now.diff(moment(e.t), 'hours') < 24);
        if (recent.length !== events.length) {
            window.localStorage.setItem(key, JSON.stringify(recent));
        }
        const usedIn24h = recent.length;
        
        // Check 15-minute cooldown from last sync
        let lastSyncTime: moment.Moment | null = null;
        let canSyncByCooldown = true;
        let nextAllowedAt: string | null = null;
        
        if (recent.length > 0) {
            // Get the most recent sync
            const sorted = [...recent].sort((a, b) => moment(b.t).valueOf() - moment(a.t).valueOf());
            lastSyncTime = moment(sorted[0].t);
            const minutesSinceLastSync = now.diff(lastSyncTime, 'minutes');
            canSyncByCooldown = minutesSinceLastSync >= 15;
            
            if (!canSyncByCooldown) {
                nextAllowedAt = lastSyncTime.clone().add(15, 'minutes').toISOString();
            }
        }
        
        // Check daily limit
        const canSyncByLimit = limit === null ? true : usedIn24h < limit;
        
        // Both conditions must be met: 15 minutes passed AND daily limit not reached
        const allowed = canSyncByCooldown && canSyncByLimit;
        
        // If daily limit is reached, calculate when the oldest sync in the window will expire
        if (!canSyncByLimit && limit !== null) {
            const sorted = [...recent].sort((a, b) => moment(a.t).valueOf() - moment(b.t).valueOf());
            const firstOfWindow = sorted[0];
            const limitExpiresAt = moment(firstOfWindow.t).clone().add(24, 'hours');
            // Use whichever is later: 15 min cooldown or 24h limit expiry
            if (!nextAllowedAt || limitExpiresAt.isAfter(moment(nextAllowedAt))) {
                nextAllowedAt = limitExpiresAt.toISOString();
            }
        }
        
        return { limit, usedIn24h, allowed, nextAllowedAt };
    };

    const recordSyncEvent = (type: 'auto'|'manual') => {
        const key = getSyncHistoryKey();
        let events: { t: string; type?: 'auto'|'manual' }[] = [];
        try {
            const raw = window.localStorage.getItem(key);
            events = raw ? JSON.parse(raw) : [];
        } catch {}
        const nowIso = new Date().toISOString();
        const now = moment(nowIso);
        const pruned = events.filter(e => now.diff(moment(e.t), 'hours') < 24);
        pruned.push({ t: nowIso, type });
        window.localStorage.setItem(key, JSON.stringify(pruned));
    };

    const canSyncConnectionsNow = (): boolean => {
        const { allowed } = getSyncLimitInfo();
        return allowed;
    };

    const doAutomaticSync = async (user: IUser) => {
        const lastItemExist = window.localStorage.getItem('lastSyncAt')
        if(lastItemExist && lastItemExist !== 'undefined'){
            const currentDate = moment();
            const syncDate = moment(lastItemExist);
            if(currentDate > syncDate.add(12, 'hours') && canSyncConnectionsNow()) {
                //auto sync
                const updateDone = await doForceUpdateSync(user, syncDate)
                if(!updateDone) syncConnectionsHandler(user, false, undefined, 'auto');
                console.log('doAutomaticSync syncConversationsHandler');
                syncConversationsHandler();
            }
        }
    }

    //todo: use this every 24 hours
    const doForceUpdateSync = async (user: IUser, lastSyncDate: any) => {
        const lastItemExist = window.localStorage.getItem('lastForcedUpdatedAt')
        if(lastSyncDate || (lastItemExist && lastItemExist !== 'undefined')){
            const currentDate = moment();
            const syncDate = moment(lastItemExist);
            if(currentDate > lastSyncDate.add(120, 'hours') || currentDate > syncDate.add(120, 'hours')) {
                //auto sync
                syncConnectionsHandler(user, true);
                return true
            }
            return false
        }
    }

    const getJobChangesHandler = async () => {
        const jobChangesResult = await getJobChanges()

        const jobChanges = extractJobTriples(String(jobChangesResult ?? ''));
        console.log('Extracted job triples:', jobChanges); 

        const lsAccess = (window.localStorage.getItem('at') || undefined) as any
        const lsRefresh = (window.localStorage.getItem('rt') || undefined) as any

        const result = await setJobChangesHandler(jobChanges, { accessToken: lsAccess, refreshToken: lsRefresh })
        console.log('jobChanges - result: ', result);
        return result
    }

    const syncConnectionsHandler = async (passedUser?: IUser, forceUpdate?: boolean, authData?: IAuthData, syncType: 'auto'|'manual' = 'manual') => {
        // Enforce daily plan limits
        if (!canSyncConnectionsNow()) {
            showErrorToast({ message: 'Sync limit reached for your plan. Try again later.' });
            return;
        }
        if(authData && authData.accessToken){
            localStorage.setItem("syncConnectionsStarted", "true")
            
            await putSessionTokens({accessToken: authData.accessToken, refreshToken: authData.refreshToken})
            await getLDAccessToken()
        }

        const result = await syncConnections(forceUpdate)
        //do somothing for the first sync
        if(result) {
            if (syncType === 'manual' && syncLinkedInContactsTaskId) {
                await completeSingleTask(syncLinkedInContactsTaskId, true, false)
            }
            setLastSyncAt(new Date().toISOString(), forceUpdate);
            setUser({ ...user, ...(passedUser ?? {}), lastSyncAt: new Date().toISOString() })
            recordSyncEvent(syncType)
            showInfoToast({message: "Connection sync started, connections should appear after the process is done"})
        } else {
            localStorage.removeItem("syncConnectionsStarted")
            showErrorToast({message: "Action failed. Ensure you're logged into LinkedIn and the extension, then try a hard refresh."})
        } 

        await getJobChangesHandler()

        localStorage.removeItem("syncConnectionsStarted")
    }

    const syncConversationsHandler = async () => {
        try {
            const queryParamsRes: any = await getInboxQueryParams()
            const voyagerMessagingGraphQL = queryParamsRes?.data ?? queryParamsRes
            if (!voyagerMessagingGraphQL?.queryId || !voyagerMessagingGraphQL?.profileId) {
                console.warn('[syncConversationsHandler] /inbox/queryParams nije vratio queryId/profileId — sync preskočen.')
                return
            }
            await syncConversations({
                voyagerMessagingGraphQL,
                filter: { archived: 'No', read: 'All' },
                query: '',
            })
        } catch (e) {
            console.warn('[syncConversationsHandler] greška:', e)
        }
    }

    const setLastSyncAt = async (lastSyncAt: string, forceUpdate?: boolean) => {
        const currentValue = window.localStorage.getItem('lastSyncAt');
        const currentValueForceUpdate = window.localStorage.getItem('lastForcedUpdatedAt');
        if(moment(lastSyncAt) > moment(currentValue && currentValue !== 'undefined' ? currentValue : 0)) window.localStorage.setItem('lastSyncAt', lastSyncAt)
        if(forceUpdate && moment(lastSyncAt) > moment(currentValueForceUpdate && currentValueForceUpdate !== 'undefined' ? currentValueForceUpdate : 0)) window.localStorage.setItem('lastForcedUpdatedAt', lastSyncAt)
    }

    const logoutHandler = async (to?: string) => {
        if (user?.email) {
            saveLastLoginUser(user);
        }

        // Try to get WorkOS logout URL for SLO (Single Logout)
        /*try {
            const workOSLogout = await workOSLogoutUrl();
            
            // If WorkOS logout URL is returned, redirect to it
            if (workOSLogout?.url) {
                localStorage.removeItem('lastSyncAt');
                setHeaderState(undefined);
                resetAllStores();
                
                // Redirect to WorkOS logout URL
                window.location.href = workOSLogout.url;
                return; // Stop execution as we're redirecting
            }
        } catch (error) {
            console.log('WorkOS logout URL not available or user not logged in via SSO');
        }*/
        
        // Standard logout flow if no WorkOS logout
        await logout(logoutApi);
        localStorage.removeItem('lastSyncAt');
        setHeaderState(undefined);
        resetAllStores();
        if (to) navigate(to);
    }

    const saveUserPreferencesHandler = async (payload: any) => {

        const result = await saveUserPreferences({preferences: {
            ...user?.preferences,
            ...payload
        }})
        result?.user && setUser(result.user)
    }

    const saveTagsAndNotesSettingsHandler = async (showAutoTags: boolean) => {
        const payload = {
            user: user,
            workspaceId: workspace?._id,
            showAutoTags: showAutoTags

        }
        const result = await saveTagsAndNotesSettings(payload)
        if(result?.success) {
            setUserWorkspaces(userWorkspaces.map((userWorkspace: any) => {
                if(workspace._id === userWorkspace.workspaceId) return { ...userWorkspace, showAutoTags }
                return {...userWorkspace}
            }))
             setWorkspace({...workspace, showAutoTags: showAutoTags })
        }
    }

    const setDisconnectedConnectionsCountHandler = async (count: any) => {
        setDisconnectedConnectionsCount(count);
    }

    const generateAPIKeyHandler = async () => {
        showSpinner()
        try {
            const { apiKey } = await generateAPIKey()
            showInfoToast({message: 'LeadDelta API key generated successfully.'})
            return apiKey
        } catch(error) {
            showErrorToast({message: 'There was a problem generating your LeadDelta API key.'})
        } finally {
            hideSpinner()
        }
    }

    const canDeleteUserAccountHandler = async () => {
        const result = await canDeleteUserAccount()
        return result?.response;
    }

    const deleteUserAccountHandler = async () => {
        const result = await deleteUserAccount()
        console.log(result);
        return result;
    }

    const revokeAPIKeyHandler = async () => {
        showSpinner()
        try {
            await revokeAPIKey()
            showInfoToast({message: 'LeadDelta API key revoked successfully.'})
        } catch(error) {
            showErrorToast({message: 'There was a problem revoking your LeadDelta API key.'})
        } finally {
            hideSpinner()
        }
    }

    // Errors are toasted by the request; a request cancelled by a newer one returns undefined silently.
    const getAPIKeysHandler = async (): Promise<IApiKeys | undefined> => getAPIKeys()

    // sendRequest does not throw, so these read `success` off the whole response.
    const generateMemberAPIKeyHandler = async (): Promise<IMemberApiKey | undefined> => {
        showSpinner()
        try {
            const result = await generateMemberAPIKey()
            if (!result?.success) {
                showErrorToast({message: result?.message || 'There was a problem generating your LeadDelta API key.'})
                return
            }
            showInfoToast({message: 'LeadDelta API key generated successfully.'})
            return result.data.memberApiKey
        } finally {
            hideSpinner()
        }
    }

    const revokeMemberAPIKeyHandler = async (userId: string) => {
        showSpinner()
        try {
            const result = await revokeMemberAPIKey(userId)
            if (!result?.success) {
                showErrorToast({message: result?.message || 'There was a problem revoking the LeadDelta API key.'})
                return false
            }
            showInfoToast({message: 'LeadDelta API key revoked successfully.'})
            return true
        } finally {
            hideSpinner()
        }
    }

    const deleteMemberAPIKeyHandler = async (userId: string) => {
        showSpinner()
        try {
            const result = await deleteMemberAPIKey(userId)
            if (!result?.success) {
                showErrorToast({message: result?.message || 'There was a problem deleting the LeadDelta API key.'})
                return false
            }
            showInfoToast({message: 'LeadDelta API key deleted successfully.'})
            return true
        } finally {
            hideSpinner()
        }
    }

    const resetAllStores = async () => {
        // reset stores
        resetConnectionsStoreHandler();
        resetTagsStoreHandler();
        resetNotesStoreHandler();
        resetTasksStoreHandler();
        resetTemplatesStoreHandler();
        resetBillingStore(); // can't use billing handler because of circular dependency
        resetWorkspaceUsersStoreHandler();
    }

    const setVerifyEmailRedirectedHandler = (verifyEmailRedirected: boolean) => {
        setVerifyEmailRedirected(verifyEmailRedirected);
    }

    const updateLastFetchPostsDateHandler = async () => {
        try {
            console.log('updateLastFetchPostsDateHandler called - starting API request...');
            const currentDate = new Date().toISOString();
            console.log('Current date to set:', currentDate);
            
            const result = await updateLastFetchPostsDate();
            console.log('API response:', result);
            
            if (result?.success) {
                updateUserLastFetchPostsDate(currentDate);
                console.log('User lastFetchPostsDate updated successfully in store');
                return true;
            } else {
                console.log('API call failed or returned no success:', result);
                return false;
            }
        } catch (error) {
            console.error('Error updating lastFetchPostsDate:', error);
            return false;
        }
    }
     
    // Test implementation of calling the mutual connections API via the LinkedIn tab.
    // Kept temporarily as reference while the main SDUI flow was being developed.
    const searchPeopleMutualConnectionsLinkedinTabHandler = async (connectionOf: string, publicIdentifier: string, connectionId?: string) => {
        const voyagerSearchDashClustersQueryId = 'ef3d0937fb65bd7812e32e5a85028e79';
        let start = 0;
        let parsed = null;
        const collectedPublicIdentifiers = new Set<string>();
        const maxPages = 25;
        const totalBudgetMs = 30_000;
        const requestTimeoutMs = 8_000;
        const minDelayMs = 1_200;
        const maxDelayMs = 2_800;
        const startedAt = Date.now();

        // Guard against unexpected API loops if LinkedIn returns wrong nextStart.
        for (let page = 0; page < maxPages; page += 1) {
            if (page > 0) {
                const delayMs = Math.floor(minDelayMs + Math.random() * (maxDelayMs - minDelayMs + 1));
                const remainingBudget = totalBudgetMs - (Date.now() - startedAt);
                const sleepMs = Math.min(delayMs, Math.max(0, remainingBudget));
                if (sleepMs > 0) {
                    await new Promise((resolve) => setTimeout(resolve, sleepMs));
                }
            }
            const elapsed = Date.now() - startedAt;
            if (elapsed >= totalBudgetMs) {
                console.warn(`[mutualConnections] Stopping pagination: total timeout (${totalBudgetMs}ms) reached`);
                break;
            }
            const response = await Promise.race([
                mutualConnectionsFromLinkedinTab({
                    connectionOf,
                    start,
                    voyagerSearchDashClustersQueryId
                }),
                new Promise((_, reject) => {
                    setTimeout(() => reject(new Error(`mutualConnections request timeout after ${requestTimeoutMs}ms`)), requestTimeoutMs);
                })
            ]);
            parsed = parseMutualConnectionsSearchResponse(response);
            parsed.connections.forEach((connection) => {
                if (!connection.publicIdentifier) return;
                if (user?.linkedinData?.publicIdentifier && connection.publicIdentifier === user.linkedinData.publicIdentifier) return;
                collectedPublicIdentifiers.add(connection.publicIdentifier);
            });
            if (!parsed.hasMore || parsed.nextStart == null || parsed.nextStart <= start) break;
            start = parsed.nextStart;
        }

        const publicIdentifiers = Array.from(collectedPublicIdentifiers);
        if (publicIdentifiers.length === 0) return { connections: [], noMutualConnections: true };

        try {
            if (connectionId) {
                await updateLinkedinMutualProfilesHandler(connectionId, publicIdentifiers);
            }
        } catch (persistError) {
            console.warn('Failed to persist linkedin mutual profiles', persistError);
        }

        const connections = await getConnectionsByPublicIdentifiersHandler(publicIdentifiers);
        return { connections, noMutualConnections: false };
    }

    const searchPeopleSduiHandler = async (connectionOf: string, publicIdentifier: string, connectionId?: string) => {
        console.log('searchPeopleSduiHandler called with connectionOf: ', connectionOf, ' and publicIdentifier: ', publicIdentifier);
        try {
            const { result } = await getProfileByIdentityDash({ vanityName: publicIdentifier, identityDashProfileByVanityQueryId: 'a3de77c32c473719f1c58fae6bff43a5' });
            if (!result?.sharedConnections) return { connections: [], noMutualConnections: true };
            const { included } = result;
            const profile = Array.isArray(included)
                ? included.find((item: any) => item?.$type === 'com.linkedin.voyager.dash.identity.profile.Profile' && item?.publicIdentifier === publicIdentifier)
                : undefined;
            const connectionOfId = profile?.entityUrn ? String(profile.entityUrn).split(':').pop() ?? connectionOf : connectionOf;
            const response = await searchPeopleSdui({ connectionOf: connectionOfId });
            const apiResult = response?.result ?? null;
            if (apiResult == null) return { connections: [], noMutualConnections: false };
            let raw = typeof apiResult.raw === "string" ? apiResult.raw : null;
            let cacheId = apiResult.cacheId ?? undefined;
            if (!raw) return { connections: [], noMutualConnections: false };
            let lastPagination = getPaginationData(raw);
            const totalPages = lastPagination.totalPages || 1;

            const allPeople = [...parsePeopleFromSdui(raw, { excludePublicIdentifier: user?.linkedinData?.publicIdentifier })];
            let pageIndex = 1;

            while (lastPagination.hasNextPage && pageIndex < totalPages && cacheId) {
                const delayMs = 2000 + Math.random() * 2000;
                console.log(`[SEARCH_PEOPLE_SDUI] Strana ${pageIndex + 1}/${totalPages}, čekam ${(delayMs / 1000).toFixed(1)}s...`);
                await new Promise((r) => setTimeout(r, delayMs));

                const nextResponse = await searchPeopleSdui({
                    connectionOf: connectionOfId,
                    cacheId,
                    nextPageBindingKey: lastPagination.nextPageBindingKey,
                    pageNumber: pageIndex,
                });
                pageIndex += 1;

                const nextResult = nextResponse?.result ?? null;
                if (nextResult == null) break;

                raw = typeof nextResult.raw === "string" ? nextResult.raw : null;
                if (nextResult.cacheId) cacheId = nextResult.cacheId;
                if (!raw) break;

                const pagePeople = parsePeopleFromSdui(raw, { excludePublicIdentifier: user?.linkedinData?.publicIdentifier });
                if (pagePeople.length) allPeople.push(...pagePeople);
                lastPagination = getPaginationData(raw);
            }
            const publicIdentifiers = allPeople.map((p) => p.publicIdentifier).filter(Boolean);

            if (publicIdentifiers.length === 0) return { connections: [], noMutualConnections: false };

            try {
                if (connectionId) {
                    await updateLinkedinMutualProfilesHandler(connectionId, publicIdentifiers);
                }
            } catch (persistError) {
                console.warn('Failed to persist linkedin mutual profiles', persistError);
            }

            const connections = await getConnectionsByPublicIdentifiersHandler(publicIdentifiers);
            console.log('connections: ', connections);
            return { connections, noMutualConnections: false };
        } catch (e) {
            console.error("searchPeopleSdui failed", e);
            throw e;
        }
    };

    const sendDesktopLinkHandler = async (source: 'home' | 'onboarding' = 'home') => {
        try {
            showSpinner()
            const response = await sendDesktopLink({ source })
            if (!response?.success) {
                showErrorToast({ message: response?.message || 'Could not send desktop link. Please try again.' })
                return response
            }
            showSuccessToast({ message: response?.message || 'Desktop link sent to your email.' })
            return response
        } catch (error) {
            console.error('sendDesktopLink failed', error)
            showErrorToast({ message: 'Could not send desktop link. Please try again.' })
        } finally {
            hideSpinner()
        }
    };

    return {
        setAuthDataHandler,
        getUserHandler,
        getMeHandler,
        getLoggedInLIUserHandler,
        createAndLoginToWorkspaceHandler,
        createWorkspaceHandler,
        getUserWorkspacesHandler,
        saveOnboardingData,
        changeUserWorkspace,
        syncConnectionsHandler,
        saveLinkedInDataToUser,
        logoutHandler,
        saveUserPreferencesHandler,
        saveTagsAndNotesSettingsHandler,
        doAutomaticSync,
        doForceUpdateSync,
        syncConversationsHandler,
        getSyncLimitInfo,
        canSyncConnectionsNow,
        setDisconnectedConnectionsCountHandler,
        generateAPIKeyHandler,
        revokeAPIKeyHandler,
        getAPIKeysHandler,
        generateMemberAPIKeyHandler,
        revokeMemberAPIKeyHandler,
        deleteMemberAPIKeyHandler,
        canDeleteUserAccountHandler,
        deleteUserAccountHandler,
        setVerifyEmailRedirectedHandler,
        updateLastFetchPostsDateHandler,
        searchPeopleSduiHandler,
        sendDesktopLinkHandler,
    }
}